/**
 * The shape of a Pokémon data snapshot: everything the shared calculations need, taken from Pokémon Showdown's
 * data at build time (see snapshot/build.ts) and stored compactly, so it can ship to browsers.
 */

/** Where a snapshot's data came from, so results can say which data they used and incompatible data isn't mixed */
export interface DataVersion {
	schema: 2;
	source: 'pokemon-showdown';
	/** The Showdown package version, and the commit of the Showdown checkout it was built from */
	showdownVersion: string;
	showdownCommit: string;
	/** Whether that checkout had uncommitted changes */
	dirty: boolean;
	/** The Showdown mod (gen9, or a custom mod) and its generation */
	mod: string;
	gen: number;
	builtAt: string;
}

export type StatTuple = [hp: number, atk: number, def: number, spa: number, spd: number, spe: number];

/** Facts about a Pokémon, as bit flags */
export const SpeciesFlag = {
	/** Not fully evolved (can use Eviolite) */
	nfe: 1,
	/** Needs a held item to exist (a Mega Stone, Primal orb, plate, mask...), so it can't hold anything else */
	needsItem: 2,
	/** A Mega Evolution (only one Pokémon can Mega Evolve in a battle) */
	mega: 4,
} as const;

/**
 * A Pokémon: name, types, base stats, abilities, sprite id, the id of the Pokémon whose learnset it shares
 * ('' for its own), SpeciesFlag flags, its base species' id ('' if it is the base species), and for battle-only
 * formes like Megas, the id of the forme it changes from
 */
export type SpeciesRow = [
	name: string, types: string[], stats: StatTuple, abilities: string[], sprite: string, learnsetFrom: string,
	flags?: number, baseSpecies?: string, changesFrom?: string,
];

/** 'P' physical, 'S' special, 'X' status */
export type CategoryCode = 'P' | 'S' | 'X';

/** Things about a move that change how strong it really is, as bit flags */
export const MoveTrait = {
	/** Must recharge next turn (Hyper Beam) */
	recharge: 1,
	/** Charges on the first turn (Solar Beam, Sky Attack) */
	charge: 2,
	/** The user faints (Explosion) */
	selfKO: 4,
	/** Recoil or crash damage */
	recoil: 8,
	/** Lowers the user's own stats (Close Combat, Draco Meteor) */
	selfDrop: 16,
	/** Hits 2-5 times (power assumes 3 hits; Skill Link makes it 5) */
	multiHit: 32,
	/** Makes contact (Tough Claws) */
	contact: 64,
	/** A punch (Iron Fist) */
	punch: 128,
	/** A bite (Strong Jaw) */
	bite: 256,
	/** An aura or pulse move (Mega Launcher) */
	pulse: 512,
	/** A sound move (Punk Rock) */
	sound: 1024,
	/** A slicing move (Sharpness) */
	slicing: 2048,
	/** Has secondary effects (Sheer Force) */
	secondary: 4096,
	/** Hits the user's ally as well as both foes (Earthquake) */
	hitsAlly: 8192,
	/** Lowers the user's attacking stat sharply, so it's a one-off (Draco Meteor, Overheat) */
	selfAttackDrop: 16384,
} as const;

/**
 * A move: name, type, category, base power, accuracy (101 = never misses), priority, whether it hits both foes
 * (1 or 0), its power for coverage (fixed for variable-power moves, every hit of multi-hit moves), and its
 * traits (MoveTrait flags)
 */
export type MoveRow = [
	name: string, type: string, category: CategoryCode, basePower: number, accuracy: number, priority: number,
	spread: number, power: number, traits: number,
];

export interface PokemonDataSnapshot {
	version: DataVersion;
	/** Attacking and defending types (Stellar is left out) */
	types: string[];
	/** attacking type -> defending type -> multiplier (0, 0.5, 1 or 2) */
	typeChart: Record<string, Record<string, number>>;
	/** Move ids, in the order learnsets refer to them */
	moveIds: string[];
	moves: Record<string, MoveRow>;
	species: Record<string, SpeciesRow>;
	/** speciesid -> the indexes (base 36, comma-separated) of the moves it can learn */
	learnsets: Record<string, string>;
}

export interface Species {
	id: string;
	name: string;
	types: string[];
	stats: { hp: number, atk: number, def: number, spa: number, spd: number, spe: number };
	bst: number;
	abilities: string[];
	sprite: string;
	/** Not fully evolved */
	nfe: boolean;
	/** Needs a held item to exist (a Mega Stone, plate, mask...), so it can't hold anything else */
	needsItem: boolean;
	/** A Mega Evolution */
	mega: boolean;
	/** For battle-only formes like Megas, the forme it changes from in battle ('' otherwise) */
	changesFrom: string;
	/** The base species' id (its own id if it is one): "charizard" for Charizard-Mega-Y */
	baseSpecies: string;
}

export interface MoveInfo {
	id: string;
	name: string;
	type: string;
	category: 'Physical' | 'Special' | 'Status';
	basePower: number;
	/** true for moves that never miss */
	accuracy: number | true;
	priority: number;
	spread: boolean;
	/** Power for coverage: fixed for variable-power moves, and every hit of multi-hit moves */
	power: number;
	/** MoveTrait flags */
	traits: number;
}

/** A Pokémon on a team, with the ability it's running */
export interface TeamMember {
	id: string;
	ability?: string;
	[extra: string]: unknown;
}
